import { useMemo, useState } from "react";

import {
  TENANT_PATHS,
  GENERIC_PATHS,
  KAKELY_PATHS,
  NOTIFICATION_PATHS,
  USER_PATHS,
  YOBELY_PATHS,
} from "@constants/NavigationPaths";
import { sideBarLabels } from "@constants/SideBarLabels";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { APP_NAMES } from "@constants/Enums";

const tenant: string | null = localStorage.getItem("realm");
const adminTenant = import.meta.env.VITE_ADMIN_REALM;

export const DefaultSidebarItems = (companyId, availableApps) => {
  const tenantApps = availableApps.map((app) => app.app_name);

  return [
    {
      title: sideBarLabels.TENANT.topTitle,
      key: 1,
      icon: (
        <svg
          width="22"
          height="20"
          viewBox="0 0 22 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.5 5H9.25M6.5 9H9.25M6.5 13H9.25M12.75 5H15.5M12.75 9H15.5M12.75 13H15.5M19 19V4.2C19 3.0799 19 2.51984 18.782 2.09202C18.5903 1.71569 18.2843 1.40973 17.908 1.21799C17.4802 1 16.9201 1 15.8 1H6.2C5.07989 1 4.51984 1 4.09202 1.21799C3.71569 1.40973 3.40973 1.71569 3.21799 2.09202C3 2.51984 3 3.0799 3 4.2V19M21 19H1"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      children: [
        {
          key: "company-info-settings",
          title: sideBarLabels.TENANT.companySettings,
          path: `/company/${companyId}/info`,
        },
      ],
    },
    {
      key: 2,
      title: sideBarLabels.USER.topTitle,
      icon: (
        <svg
          width="22"
          height="21"
          viewBox="0 0 22 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 13H7C5.13623 13 4.20435 13 3.46927 13.3045C2.48915 13.7105 1.71046 14.4892 1.30448 15.4693C1 16.2044 1 17.1362 1 19M14.5 1.29076C15.9659 1.88415 17 3.32131 17 5M10.9999 19.5L13.025 19.095C13.2015 19.0597 13.2898 19.042 13.3721 19.0097C13.4452 18.9811 13.5147 18.9439 13.579 18.899C13.6516 18.8484 13.7152 18.7848 13.8426 18.6574L20.5 12C21.0524 11.4477 21.0523 10.5523 20.5 9.99997C19.9477 9.4477 19.0523 9.44771 18.5 9.99999L11.8425 16.6575C11.7152 16.7848 11.6516 16.8484 11.601 16.921C11.5561 16.9853 11.5189 17.0548 11.4902 17.1278C11.458 17.2102 11.4403 17.2984 11.405 17.475L10.9999 19.5ZM12.5 5C12.5 7.20914 10.7091 9 8.5 9C6.29086 9 4.5 7.20914 4.5 5C4.5 2.79086 6.29086 1 8.5 1C10.7091 1 12.5 2.79086 12.5 5Z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      children: [
        {
          key: "user-view",
          title: sideBarLabels.USER.view,
          path: `${USER_PATHS.main}`,
        },
        {
          key: "user-add",
          title: sideBarLabels.USER.create,
          path: `${USER_PATHS.createUser}`,
        },
        {
          key: "user-csv",
          title: sideBarLabels.USER.csvUpload,
          path: `${USER_PATHS.csvUpload}`,
        },
      ],
    },
    tenantApps.includes(APP_NAMES.KAKELY) && {
      key: 3,
      title: sideBarLabels.KAKELY.topTitle,
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.7143 17.4V20.28M20 21H6.28571C5.14286 21 4 20.28 4 18.84M4 18.84C4 17.4 5.14286 16.68 6.28571 16.68H20V3H6.28571C5.14286 3 4 4.08 4 5.16V18.84Z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      children: [
        {
          key: "department-csv",
          title: sideBarLabels.KAKELY.departmentView,
          path: `${KAKELY_PATHS.allDepartmentSettings}`,
        },
        {
          key: "external-phonebook",
          title: sideBarLabels.KAKELY.externalPhonebookView,
          children: [
            {
              key: "external-phonebook-view",
              title: sideBarLabels.KAKELY.viewAllPhonebooks,
              path: `${KAKELY_PATHS.main}`,
            },
            {
              key: "external-phonebook-csv",
              title: sideBarLabels.KAKELY.csvUploadExternal,
              path: `${KAKELY_PATHS.csvUploadExternal}`,
            },
          ],
        },
      ],
    },
    tenantApps.includes(APP_NAMES.YOBELY) && {
      key: 4,
      title: sideBarLabels.YOBELY.topTitle,
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.4901 20.0119C19.6681 20.9619 18.4931 21.5 17.2491 21.5C17.2101 21.5 17.1711 21.5 17.1321 21.499C9.14408 21.282 2.71707 14.8549 2.50207 6.86789C2.46707 5.57089 3.01308 4.34187 3.99908 3.49387C4.93608 2.68687 6.15608 2.34996 7.34808 2.56296C7.53108 2.59596 7.68808 2.63785 7.82208 2.68185C8.35008 2.85685 8.73408 3.31404 8.82208 3.87204L9.49213 8.12692C9.57613 8.65992 9.36811 9.20006 8.95111 9.53806C8.86711 9.60606 8.78009 9.67093 8.68909 9.73093C8.45709 9.88893 8.36908 10.1579 8.47308 10.3969C9.47508 12.7019 11.2951 14.5219 13.5991 15.5239C13.8391 15.6289 14.1081 15.539 14.2701 15.302C14.3171 15.234 14.3661 15.167 14.4161 15.103C14.7521 14.675 15.3031 14.4689 15.8471 14.5559L20.1791 15.2771C20.7511 15.3731 21.2161 15.778 21.3651 16.31C21.3921 16.407 21.4181 16.5171 21.4401 16.6421C21.6491 17.8431 21.3031 19.0699 20.4901 20.0119Z"
            stroke="white"
            strokeWidth="2"
          />
        </svg>
      ),
      children: [
        {
          key: "broadcast-groups",
          title: sideBarLabels.YOBELY.viewGroups,
          path: `${YOBELY_PATHS.main}`,
        },
      ],
    },
    {
      key: 6,
      title: sideBarLabels.GENERIC.infoTopTitle,
      icon: <InformationCircleIcon className="w-6 h-6" />,
      path: `${GENERIC_PATHS.home}`,
      state: { fromHomePage: true },
    },
  ].filter(Boolean);
};

// sidebar items used for admin users
export const AdminSidebarItems = (companyId) => [
  {
    title: sideBarLabels.TENANT.topTitle,
    key: 10,
    icon: (
      <svg
        width="22"
        height="20"
        viewBox="0 0 22 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.5 5H9.25M6.5 9H9.25M6.5 13H9.25M12.75 5H15.5M12.75 9H15.5M12.75 13H15.5M19 19V4.2C19 3.0799 19 2.51984 18.782 2.09202C18.5903 1.71569 18.2843 1.40973 17.908 1.21799C17.4802 1 16.9201 1 15.8 1H6.2C5.07989 1 4.51984 1 4.09202 1.21799C3.71569 1.40973 3.40973 1.71569 3.21799 2.09202C3 2.51984 3 3.0799 3 4.2V19M21 19H1"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    children: [
      {
        key: "company-management-view",
        title: sideBarLabels.TENANT.viewCompanies,
        path: `${TENANT_PATHS.listCompanies}`,
      },
      {
        key: "company-settings",
        title: sideBarLabels.TENANT.companySettings,
        path: `/company/${companyId}/manage`,
      },
      ...(tenant === adminTenant
        ? [
            {
              title: sideBarLabels.TENANT.createCompany,
              path: `${TENANT_PATHS.createCompany}`,
            },
          ]
        : []),
    ],
  },
  {
    key: 11,
    title: sideBarLabels.USER.topTitle,
    icon: (
      <svg
        width="22"
        height="21"
        viewBox="0 0 22 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 13H7C5.13623 13 4.20435 13 3.46927 13.3045C2.48915 13.7105 1.71046 14.4892 1.30448 15.4693C1 16.2044 1 17.1362 1 19M14.5 1.29076C15.9659 1.88415 17 3.32131 17 5M10.9999 19.5L13.025 19.095C13.2015 19.0597 13.2898 19.042 13.3721 19.0097C13.4452 18.9811 13.5147 18.9439 13.579 18.899C13.6516 18.8484 13.7152 18.7848 13.8426 18.6574L20.5 12C21.0524 11.4477 21.0523 10.5523 20.5 9.99997C19.9477 9.4477 19.0523 9.44771 18.5 9.99999L11.8425 16.6575C11.7152 16.7848 11.6516 16.8484 11.601 16.921C11.5561 16.9853 11.5189 17.0548 11.4902 17.1278C11.458 17.2102 11.4403 17.2984 11.405 17.475L10.9999 19.5ZM12.5 5C12.5 7.20914 10.7091 9 8.5 9C6.29086 9 4.5 7.20914 4.5 5C4.5 2.79086 6.29086 1 8.5 1C10.7091 1 12.5 2.79086 12.5 5Z"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    children: [
      {
        key: "view-users",
        title: sideBarLabels.USER.view,
        path: `${USER_PATHS.main}`,
      },
      {
        key: "create-user",
        title: sideBarLabels.USER.create,
        path: `${USER_PATHS.createUser}`,
      },
    ],
  },
  {
    key: 12,
    title: sideBarLabels.NOTIFICATION.topTitle,
    icon: <InformationCircleIcon className="w-6 h-6" />,
    children: [
      {
        title: sideBarLabels.NOTIFICATION.view,
        key: "view-notifications",
        path: `${NOTIFICATION_PATHS.main}`,
        state: { fromHomePage: false },
      },
      {
        title: sideBarLabels.NOTIFICATION.create,
        path: `${NOTIFICATION_PATHS.createNotification}`,
      },
    ],
  },
];

export const myUserWithPasswordItems = (userId) => [
  {
    key: 8,
    title: sideBarLabels.USER.personalTopTile,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.25 15C4.00184 13.1419 6.38026 12 9 12C11.6197 12 13.9982 13.1419 15.75 15M12.375 5.625C12.375 7.48896 10.864 9 9 9C7.13604 9 5.625 7.48896 5.625 5.625C5.625 3.76104 7.13604 2.25 9 2.25C10.864 2.25 12.375 3.76104 12.375 5.625Z"
          stroke="white"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    children: [
      {
        key: "current-user",
        title: sideBarLabels.USER.myPage,
        path: `${USER_PATHS.myPage}`,
      },
      {
        key: "password-change",
        title: sideBarLabels.USER.passwordChange,
        path: `${USER_PATHS.passwordReset}`,
      },
    ],
  },
];
